import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface KeyboardShortcutsOptions {
  onPlayPause?: () => void;
  videoElement?: HTMLVideoElement | null;
  onShowHelp?: () => void;
}

export const useKeyboardShortcuts = (options: KeyboardShortcutsOptions = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { onPlayPause, videoElement, onShowHelp } = options;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when user is typing in input fields
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      // Handle different key combinations
      const { key, ctrlKey, metaKey, altKey: _altKey } = event;
      const isModifierPressed = ctrlKey || metaKey; // Support both Ctrl (Windows/Linux) and Cmd (Mac)

      switch (true) {
        // Navigation shortcuts
        case isModifierPressed && key === 'd':
          event.preventDefault();
          navigate('/dashboard');
          break;

        case isModifierPressed && key === 'u':
          event.preventDefault();
          navigate('/upload');
          break;

        case isModifierPressed && key === 'a':
          event.preventDefault();
          navigate('/analytics');
          break;

        case isModifierPressed && key === 'h':
          event.preventDefault();
          navigate('/');
          break;

        case isModifierPressed && key === '?':
          event.preventDefault();
          if (onShowHelp) {
            onShowHelp();
          }
          break;

        // Video controls
        case key === ' ' || key === 'Spacebar':
          event.preventDefault();
          if (videoElement) {
            if (videoElement.paused) {
              videoElement.play();
            } else {
              videoElement.pause();
            }
          } else if (onPlayPause) {
            onPlayPause();
          }
          break;

        case key === 'k':
          event.preventDefault();
          if (videoElement) {
            if (videoElement.paused) {
              videoElement.play();
            } else {
              videoElement.pause();
            }
          } else if (onPlayPause) {
            onPlayPause();
          }
          break;

        case key === 'ArrowLeft':
          event.preventDefault();
          if (videoElement) {
            videoElement.currentTime = Math.max(0, videoElement.currentTime - 10);
          }
          break;

        case key === 'ArrowRight':
          event.preventDefault();
          if (videoElement) {
            videoElement.currentTime = Math.min(videoElement.duration, videoElement.currentTime + 10);
          }
          break;

        case key === 'ArrowUp':
          event.preventDefault();
          if (videoElement) {
            videoElement.volume = Math.min(1, videoElement.volume + 0.1);
          }
          break;

        case key === 'ArrowDown':
          event.preventDefault();
          if (videoElement) {
            videoElement.volume = Math.max(0, videoElement.volume - 0.1);
          }
          break;

        case key === 'm':
          event.preventDefault();
          if (videoElement) {
            videoElement.muted = !videoElement.muted;
          }
          break;

        case key === 'f':
          event.preventDefault();
          if (videoElement) {
            if (document.fullscreenElement) {
              document.exitFullscreen();
            } else {
              videoElement.requestFullscreen();
            }
          }
          break;

        // Escape key to close modals or go back
        case key === 'Escape':
          event.preventDefault();
          // Check if we're on a detail page and can go back
          if (location.pathname.includes('/videos/') || location.pathname.includes('/clips/')) {
            navigate(-1);
          }
          break;

        default:
          break;
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigate, location, onPlayPause, videoElement, onShowHelp]);

  // Return shortcut info for help display
  return {
    shortcuts: [
      { key: 'Ctrl+D', description: 'Go to Dashboard' },
      { key: 'Ctrl+U', description: 'Go to Clipper' },
      { key: 'Ctrl+A', description: 'Go to Analytics' },
      { key: 'Ctrl+H', description: 'Go to Home' },
      { key: 'Ctrl+?', description: 'Show keyboard shortcuts' },
      { key: 'Space/K', description: 'Play/Pause video' },
      { key: '←/→', description: 'Skip 10s backward/forward' },
      { key: '↑/↓', description: 'Volume up/down' },
      { key: 'M', description: 'Mute/Unmute' },
      { key: 'F', description: 'Fullscreen' },
      { key: 'Esc', description: 'Go back/Close' },
    ]
  };
}; 